import React from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { BarChartOutlined } from "@ant-design/icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import MainCard from "components/MainCard";
import { IEntryExitTrendItem } from "../api/types";

interface TrendChartProps {
  data?: IEntryExitTrendItem[];
  isLoading: boolean;
  isSingleDay: boolean;
}

export default function TrendChart({
  data,
  isLoading,
  isSingleDay,
}: TrendChartProps) {
  const chartData = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map((d) => ({
      name: d.timeLabel,
      Entries: d.entryCount,
      Exits: d.exitCount,
    }));
  }, [data]);

  const hasData = chartData.length > 0;

  return (
    <MainCard sx={{ mt: 1, height: "100%" }}>
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={12}>
          <Typography
            variant="h4"
            component="h3"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontWeight: 600,
            }}
          >
            <BarChartOutlined style={{ fontSize: 22, color: "#1890ff" }} />
            {isSingleDay
              ? "Hourly Traffic (Entries vs Exits)"
              : "Daily Traffic Trend"}
          </Typography>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ height: { xs: 280, sm: 340, md: 380 }, width: "100%" }}>
        {isLoading ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : !hasData ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">
              No entry/exit activity recorded for this period.
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{
                top: 10,
                right: 15,
                left: -15,
                bottom: isSingleDay ? 5 : 35,
              }}
            >
              <XAxis
                dataKey="name"
                interval="preserveStartEnd"
                angle={isSingleDay ? 0 : -30}
                textAnchor={isSingleDay ? "middle" : "end"}
                height={isSingleDay ? 30 : 55}
                tick={{ fontSize: 10 }}
              />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Entries" fill="#1890ff" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Exits" fill="#fa8c16" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </MainCard>
  );
}

